import { keyframes, styled } from "@mui/material";

const floatBlob = keyframes({
  "0%, 100%": { transform: "translate(0, 0) scale(1)" },
  "50%": { transform: "translate(24px, -30px) scale(1.08)" },
});

const pulse = keyframes({
  "0%": { boxShadow: "0 0 0 0 rgba(46, 155, 217, 0.55)" },
  "70%": { boxShadow: "0 0 0 10px rgba(46, 155, 217, 0)" },
  "100%": { boxShadow: "0 0 0 0 rgba(46, 155, 217, 0)" },
});

const growBar = keyframes({
  from: { transform: "scaleY(0)" },
  to: { transform: "scaleY(1)" },
});

export const StyledHero = {
  Root: styled("section")(({ theme }) => ({
    position: "relative",
    paddingTop: "calc(74px + clamp(40px, 8vw, 90px))",
    paddingBottom: "clamp(40px, 6vw, 80px)",
    overflow: "hidden",
    background:
      "radial-gradient(1200px 600px at 80% -10%, rgba(23, 79, 155, 0.10), transparent 60%), radial-gradient(900px 500px at 0% 20%, rgba(75, 163, 224, 0.08), transparent 55%), #ffffff",
  })),

  Bg: styled("div")({
    position: "absolute",
    inset: 0,
    zIndex: 0,
    pointerEvents: "none",
  }),

  Blob: styled("span", {
    shouldForwardProp: (prop) => prop !== "variant",
  })(({ variant }) => {
    const map = {
      1: {
        width: 460,
        height: 460,
        top: -120,
        right: -80,
        background: "radial-gradient(circle, #2E6FD6, transparent 65%)",
        animationDelay: "0s",
      },
      2: {
        width: 380,
        height: 380,
        top: 120,
        left: -120,
        background: "radial-gradient(circle, #4BA3E0, transparent 65%)",
        animationDelay: "-4s",
      },
      3: {
        width: 300,
        height: 300,
        bottom: -80,
        left: "40%",
        background: "radial-gradient(circle, #2B5CB8, transparent 65%)",
        animationDelay: "-8s",
      },
    };
    return {
      position: "absolute",
      borderRadius: "50%",
      filter: "blur(70px)",
      opacity: 0.5,
      animation: `${floatBlob} 16s ease-in-out infinite`,
      ...map[variant],
    };
  }),

  Grid: styled("div")({
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(to right, rgba(20, 32, 58, 0.045) 1px, transparent 1px), linear-gradient(to bottom, rgba(20, 32, 58, 0.045) 1px, transparent 1px)",
    backgroundSize: "46px 46px",
    maskImage: "radial-gradient(1000px 600px at 60% 0%, #000, transparent 75%)",
  }),

  Inner: styled("div")({
    position: "relative",
    zIndex: 1,
    display: "grid",
    gridTemplateColumns: "1.05fr 0.95fr",
    gap: "clamp(32px, 5vw, 72px)",
    alignItems: "center",
    "@media (max-width: 960px)": { gridTemplateColumns: "1fr" },
  }),

  Content: styled("div")({}),

  Eyebrow: styled("span")(({ theme }) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    fontFamily: theme.custom.fonts.display,
    fontWeight: 600,
    fontSize: "0.86rem",
    color: theme.custom.colors.indigo600,
    padding: "8px 15px",
    borderRadius: theme.custom.radius.full,
    background: "rgba(255, 255, 255, 0.7)",
    border: `1px solid ${theme.custom.colors.border}`,
    boxShadow: theme.custom.shadow.sm,
  })),

  Pulse: styled("span")({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: "#2E9BD9",
    animation: `${pulse} 2s infinite`,
  }),

  Title: styled("h1")(({ theme }) => ({
    marginTop: 22,
    fontFamily: theme.custom.fonts.display,
    fontWeight: 700,
    fontSize: "clamp(2.4rem, 5.6vw, 4rem)",
    lineHeight: 1.05,
    letterSpacing: "-0.02em",
    color: theme.palette.text.primary,
  })),

  Lead: styled("p")(({ theme }) => ({
    marginTop: 22,
    fontSize: "clamp(1.05rem, 2vw, 1.28rem)",
    color: theme.custom.colors.textSoft,
    maxWidth: "52ch",
  })),

  Actions: styled("div")({
    marginTop: 32,
    display: "flex",
    flexWrap: "wrap",
    gap: 14,
  }),

  Pills: styled("ul")({
    marginTop: 30,
    listStyle: "none",
    padding: 0,
    display: "flex",
    flexWrap: "wrap",
    gap: "10px 22px",
    "& li": {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      fontSize: "0.92rem",
      fontWeight: 500,
      color: "#4b587a",
    },
    "& li svg": { color: "#2E9BD9", flexShrink: 0, fontSize: "1.15rem" },
  }),

  Visual: styled("div")({
    position: "relative",
    "@media (max-width: 960px)": {
      maxWidth: 520,
      marginInline: "auto",
      width: "100%",
    },
  }),

  Chip: styled("div", {
    shouldForwardProp: (prop) => prop !== "placement",
  })(({ theme, placement }) => ({
    position: "absolute",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    fontSize: "0.82rem",
    fontWeight: 600,
    color: theme.palette.text.primary,
    background: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(8px)",
    padding: "10px 15px",
    borderRadius: theme.custom.radius.full,
    boxShadow: theme.custom.shadow.md,
    border: `1px solid ${theme.custom.colors.border}`,
    "& svg": { color: theme.custom.colors.indigo600, fontSize: "1.1rem" },
    ...(placement === "top"
      ? { top: "14%", left: -26 }
      : { bottom: "16%", right: -18 }),
    "@media (max-width: 520px)": placement === "top" ? { left: 0 } : { right: 0 },
  })),

  Stats: styled("div")(({ theme }) => ({
    marginTop: "clamp(48px, 7vw, 84px)",
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 20,
    padding: "30px clamp(20px, 4vw, 44px)",
    borderRadius: theme.custom.radius.lg,
    background: "rgba(255, 255, 255, 0.6)",
    border: `1px solid ${theme.custom.colors.border}`,
    boxShadow: theme.custom.shadow.sm,
    backdropFilter: "blur(6px)",
    "@media (max-width: 960px)": { gridTemplateColumns: "repeat(2, 1fr)" },
  })),

  Stat: styled("div")({ textAlign: "center" }),

  StatNum: styled("div")(({ theme }) => ({
    fontFamily: theme.custom.fonts.display,
    fontWeight: 800,
    fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
    lineHeight: 1,
    background: theme.custom.gradient.brand,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    color: "transparent",
  })),

  StatLabel: styled("div")(({ theme }) => ({
    marginTop: 8,
    fontSize: "0.9rem",
    color: theme.custom.colors.textSoft,
    fontWeight: 500,
  })),

  // ---- Latido (ECG) integrado ----
  Ecg: styled("div")({
    position: "relative",
    width: "100%",
    maskImage:
      "linear-gradient(to right, transparent, #000 14%, #000 86%, transparent)",
    WebkitMaskImage:
      "linear-gradient(to right, transparent, #000 14%, #000 86%, transparent)",
    "& svg": { display: "block", width: "100%", height: "auto", overflow: "visible" },
    "& .glow": {
      fill: "none",
      stroke: "#4BA3E0",
      strokeWidth: 6,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      filter: "url(#ecgGlow)",
      opacity: 0.12,
    },
    "& .line": {
      fill: "none",
      stroke: "url(#ecgGrad)",
      strokeWidth: 1.8,
      strokeLinecap: "round",
      strokeLinejoin: "round",
      opacity: 0.5,
    },
  }),
};
